import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../card';
import { Button } from '../../button/button';
import { CopyLinkButton } from '../../copy-link-button/copy-link-button';
import { ChevronDown, ChevronUp, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { HideExamplesButton } from '../../hide-button/hide-examples-button';
import { PersonaAdversarialPopupButton } from '../../persona-adversarial-popup-button/persona-adversarial-popup-button';
import { SectorPill } from '../../sector-pill/sector-pill';
import evaluatorBuilderData from '../../../../data/evaluatorBuilder/data.json';
import { Markdown } from '../../markdown';
import type { ExistingEvaluatorsProps } from './types';

const COLLAPSE_THRESHOLD = 150;

export function ExistingEvaluators({
  evaluators,
  editingEvaluator,
  isAdmin,
  isOnboardingActive,
  isExampleEvaluatorsHidden,
  isTemplate,
  onEdit,
  onDelete,
  onToggleHideExamples,
  workspaceId,
}: ExistingEvaluatorsProps): JSX.Element {
  const [expandedStatements, setExpandedStatements] = useState<Set<string>>(
    new Set(),
  );

  const toggleStatement = (id: string) => {
    setExpandedStatements((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className={isAdmin ? 'lg:w-1/2' : 'w-full'}>
      <Card
        className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]"
        data-onboarding="evaluator-list"
      >
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
            Existing evaluators
          </CardTitle>
          <HideExamplesButton
            isHidden={isExampleEvaluatorsHidden}
            clickHandler={onToggleHideExamples}
          />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {evaluators.map((evaluator) => {
              if (isExampleEvaluatorsHidden && evaluator.is_example)
                return null;
              const isLong = evaluator.statement.length > COLLAPSE_THRESHOLD;
              const isExpanded = expandedStatements.has(evaluator.id);
              const canSelectEvaluator =
                isAdmin && (!evaluator.is_example || isOnboardingActive);
              return (
                <div
                  key={evaluator.id}
                  data-onboarding={
                    canSelectEvaluator ? 'evaluator-choice' : undefined
                  }
                  className={`rounded-lg border border-[#00000020] bg-prompt-safecomwhite p-4 ${
                    editingEvaluator?.id === evaluator.id
                      ? 'ring-2 ring-prompt-safecomoutrageous-orange'
                      : ''
                  } ${evaluator.is_example ? 'border-l-4 border-l-prompt-safecomoutrageous-orange' : ''} ${canSelectEvaluator ? 'cursor-pointer' : ''}`}
                  onClick={() => canSelectEvaluator && onEdit(evaluator)}
                  role={canSelectEvaluator ? 'button' : undefined}
                  tabIndex={canSelectEvaluator ? 0 : undefined}
                >
                  {/* Wraps so the buttons drop below the name in a narrow panel
                      instead of spilling out of the card. */}
                  <div className="mb-2 flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
                    <div className="min-w-0">
                      <div className="items-center gap-2 md:flex">
                        <h3 className="font-semibold text-prompt-safecomvulcan">
                          {evaluator.name}
                        </h3>
                        {evaluator.is_example && (
                          <span className="bg-prompt-safecomoutrageous-orange/10 rounded-full text-xs text-prompt-safecomoutrageous-orange md:px-2 md:py-0.5">
                            Example
                          </span>
                        )}
                        {isTemplate && (
                          <SectorPill sectorId={evaluator.sector_id} />
                        )}
                      </div>
                    </div>
                    <div className="ml-auto flex gap-2">
                      {isLong && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-prompt-safecomoutrageous-orange"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStatement(evaluator.id);
                          }}
                          tooltipContent={
                            isExpanded
                              ? evaluatorBuilderData.collapseStatementButton
                                  ?.tooltip
                              : evaluatorBuilderData.expandStatementButton
                                  ?.tooltip
                          }
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {isAdmin && (
                        <PersonaAdversarialPopupButton
                          workspaceId={workspaceId}
                          evaluatorId={evaluator?.id}
                          statement={evaluator?.statement}
                          dataOnboarding="evaluator-adversarial-generator"
                          tooltipContent="Generate an adversarial persona aimed at this evaluator."
                        />
                      )}
                      {!evaluator.is_example && (
                        <>
                          <CopyLinkButton
                            path={`/evaluator-builder/${evaluator.id}`}
                            tooltipText={
                              evaluatorBuilderData.linkEvaluatorButton?.tooltip
                            }
                          />
                          {isAdmin && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-prompt-safecomoutrageous-orange"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(evaluator);
                                }}
                                tooltipContent={
                                  evaluatorBuilderData.editEvaluatorButton
                                    ?.tooltip
                                }
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-prompt-safecomabbey hover:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(evaluator.id);
                                }}
                                tooltipContent={
                                  evaluatorBuilderData.deleteEvaluatorButton
                                    ?.tooltip
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <div
                      className={
                        isLong && !isExpanded ? 'max-h-24 overflow-hidden' : ''
                      }
                    >
                      <Markdown
                        content={evaluator.statement}
                        className="text-sm text-prompt-safecomabbey"
                      />
                    </div>
                    {isLong && !isExpanded && (
                      <div className="pointer-events-none absolute bottom-0 left-0 h-8 w-full bg-gradient-to-t from-white to-transparent" />
                    )}
                  </div>
                  <p className="mt-2 text-xs text-prompt-safecomabbey">
                    Last modified:{' '}
                    {format(new Date(evaluator.updated_at), 'yyyy-MM-dd')}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
